/**
 * External dependencies
 */
import React, { Component } from 'react';
import { injectStripe, CardElement } from 'react-stripe-elements';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import axios from 'axios';
import classNames from 'classnames';

/**
 * Internal depdendencies
 */
import Loader from '../../loader';
import InternalLink from "../../internal-link";

class Form extends Component {
    constructor( props ) {
        super( props );

        this.state = {
            fontSize: '15px',
            lineHeight: 1.8,
            message: '',
            status: '',
            loading: false,
        };
    }

    componentDidMount() {
        this.setOptions();
        window.addEventListener( 'resize', this.setOptions );
    }

    componentWillUnmount() {
        window.removeEventListener( 'resize', this.setOptions );
    }

    handleFormFieldChange = ( event ) => {
        this.setState( { [ event.target.name ]: event.target.value } );
    };

    handleFormSubmit = ( event ) => {
        event.preventDefault();

        const formEl = event.target;

        const {
            'full-name-mentorship-sign-up': name,
            'email-address-mentorship-sign-up': email,
            'password-mentorship-sign-up': password,
            'confirm-password-mentorship-sign-up': confirmPassword,
        } = this.state;

        if ( ! name ) {
            this.setState( {
                message: 'Please enter your name.',
                status: 'error',
                loading: false,
            } );

            return;
        }

        if ( ! email ) {
            this.setState( {
                message: 'Please enter a valid email address.',
                status: 'error',
                loading: false,
            } );

            return;
        }

        if ( ! password || ! confirmPassword ) {
            this.setState( {
                message: 'Please fill both password fields.',
                status: 'error',
                loading: false,
            } );

            return;
        }

        if ( password !== confirmPassword ) {
            this.setState( {
                message: 'Your passwords do not match.',
                status: 'error',
                loading: false,
            } );

            return;
        }

        const {
            stripe,
        } = this.props;

        if ( stripe ) {
            stripe.createToken().then( ( result ) => {
                if ( result.error ) {
                    this.setState( {
                        message: result.error.message,
                        loading: false,
                    } );
                } else {
                    this.handleStripeToken( result.token, formEl );
                }
            } );
        } else {
            console.log( 'Stripe.js hasn\'t loaded yet.' );
        }
    };

    handleStripeToken = ( token, formEl ) => {
        const {
            stripe,
            isOffer,
            planOption,
        } = this.props;

        const {
            'full-name-mentorship-sign-up': name,
            'email-address-mentorship-sign-up': email,
            'password-mentorship-sign-up': password,
        } = this.state;

        this.setState( {
            loading: true,
        } );

        let args = {
            token,
            email,
            name,
            password,
            planOption,
        };

        if ( isOffer ) {
            args = {
                ...args,
                isOffer,
            };
        }

        axios.post( `${process.env.GATSBY_BACK_END_URL}/mentorship-transaction`, args ).then( ( response ) => {
            if ( response && response.data ) {
                const {
                    status,
                    message,
                    clientSecret,
                } = response.data;

                this.setState( {
                    status,
                    message,
                    loading: status === 'requires_action',
                } );

                if ( status === 'success' ) {
                    formEl.reset();
                    this.cardElement.clear();
                }

                if ( status === 'requires_action' && clientSecret ) {
                    stripe.handleCardPayment( clientSecret ).then( ( result ) => {
                        if ( result.error ) {
                            this.setState( {
                                status: 'error',
                                message: result.error.message,
                                loading: false,
                            } );
                        } else {
                            this.setState( {
                                status: 'success',
                                message: 'Welcome aboard! You\'ve successfully signed up and I\'ll be in touch soon.',
                                loading: false,
                            } );

                            formEl.reset();
                            this.cardElement.clear();
                        }
                    } );
                }
            }
        } ).catch( ( error ) => {
            console.log( error );
        } );
    };

    setOptions = () => {
        if ( window.outerWidth > 1023 ) {
            this.setState( {
                fontSize: '18px',
                lineHeight: 1.2,
            } );
        } else if ( window.outerWidth > 767 ) {
            this.setState( {
                fontSize: '16px',
                lineHeight: 1.5,
            } );
        } else {
            this.setState( {
                fontSize: '15px',
                lineHeight: 1.7,
            } );
        }
    };

    render() {
        const {
            fontSize,
            lineHeight,
            message,
            status,
            loading,
        } = this.state;

        const stripeElementOptions = {
            style: {
                base: {
                    fontSize,
                    color: '#333333',
                    fontFamily: 'Open Sans, sans-serif',
                    '::placeholder': {
                        color: '#666666',
                    },
                    lineHeight,
                },
                invalid: {
                    color: '#666666',
                },
            },
        };

        const formMessageTextClasses = classNames( 'form-message-text', status ? `is-${status}` : '' );

        return (
            <div className="form" id="mentorship-sign-up-form">
                <form onSubmit={ this.handleFormSubmit }>
                    <fieldset>
                        <div className="input-wrap">
                            <label htmlFor="full-name-mentorship-sign-up">
                                Full name
                                <input
                                    name="full-name-mentorship-sign-up"
                                    id="full-name-mentorship-sign-up"
                                    placeholder="Your name"
                                    type="text"
                                    onChange={ this.handleFormFieldChange }
                                    required
                                />
                            </label>
                        </div>
                        <div className="input-wrap">
                            <label htmlFor="email-address-mentorship-sign-up">
                                Email address
                                <input
                                    name="email-address-mentorship-sign-up"
                                    id="email-address-mentorship-sign-up"
                                    placeholder="Your email address"
                                    type="email"
                                    onChange={ this.handleFormFieldChange }
                                    required
                                />
                            </label>
                        </div>
                        <div className="input-wrap">
                            <label htmlFor="password-mentorship-sign-up">
                                Password
                                <input
                                    name="password-mentorship-sign-up"
                                    id="password-mentorship-sign-up"
                                    placeholder="Choose a password"
                                    type="password"
                                    onChange={ this.handleFormFieldChange }
                                    autoComplete="new-password"
                                    pattern=".{6,}"
                                    title="6 characters minimum"
                                    required
                                />
                            </label>
                        </div>
                        <div className="input-wrap">
                            <label htmlFor="confirm-password-mentorship-sign-up">
                                Password again
                                <input
                                    name="confirm-password-mentorship-sign-up"
                                    id="confirm-password-mentorship-sign-up"
                                    placeholder="Repeat password"
                                    type="password"
                                    onChange={ this.handleFormFieldChange }
                                    pattern=".{6,}"
                                    title="6 characters minimum"
                                    required
                                />
                            </label>
                        </div>
                        <div className="input-wrap">
                            <label htmlFor="card-details-mentorship-sign-up">
                                Credit card
                                <CardElement
                                    id="card-details-mentorship-sign-up"
                                    name="card-details-mentorship-sign-up"
                                    onReady={ cardElement => this.cardElement = cardElement }
                                    { ...stripeElementOptions }
                                />
                            </label>
                        </div>
                        <div className="input-wrap d-md-flex align-items-md-center">
                            <button className="button is-green has-icon flex-md-shrink-0" type="submit">
                                Enrol now!
                                <FontAwesomeIcon icon="arrow-right" />
                            </button>
                            <span className="form-button-aside-text">
                                { ( loading && <Loader /> ) || ( ! message ? 'Limited positions available' : '' ) }
                            </span>
                        </div>
                        { message && (
                            <div className="input-wrap">
                                <span className={ formMessageTextClasses }>
                                    { message }&nbsp;
                                    { status === 'success' && <InternalLink to="/login/">Login to your account</InternalLink> }
                                </span>
                            </div>
                        ) }
                    </fieldset>
                </form>
            </div>
        );
    }
}

export default injectStripe( Form );
